import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { 
  CheckCircle, XCircle, ArrowRight, RefreshCw, Search, 
  Clock, AlertTriangle, X, ChevronRight, Loader, CreditCard, 
  ShieldCheck, Coins, CheckCircle2, AlertCircle
} from 'lucide-react';
import { PaykuService } from '../services/paykuService';

const VerifyPayment: React.FC = () => {
  const [transactionId, setTransactionId] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(5);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [isClearing, setIsClearing] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isAutoVerifying, setIsAutoVerifying] = useState<boolean>(false);
  const [verificationAttempts, setVerificationAttempts] = useState<number>(0);
  const [packageInfo, setPackageInfo] = useState<any>(null);
  const [comingFromPayku, setComingFromPayku] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Verificar si venimos desde Payku (para mostrar mensajes diferentes)
    const referrer = document.referrer;
    const isFromPayku = referrer.includes('payku.cl') || referrer.includes('webpay') || referrer.includes('transbank');
    setComingFromPayku(isFromPayku);
    
    if (isFromPayku) {
      toast.success('¡Pago recibido! Verificando la transacción...', { 
        duration: 5000,
        icon: '🔄'
      });
    }
    
    // Escuchar mensajes desde la ventana de pago
    const handlePaymentMessage = (event: MessageEvent) => {
      console.log('Mensaje recibido de ventana externa:', event.data);
      
      if (event.data && event.data.type === 'PAYMENT_COMPLETED') {
        console.log('Mensaje de pago completado recibido:', event.data);
        toast.success('Notificación de pago recibida. Verificando...', {
          duration: 3000,
          icon: '🔄'
        });
        
        // Si tenemos un ID de transacción, verificarlo
        if (event.data.txId) {
          verifyPayment(event.data.txId);
        } else {
          // Intentar verificar el último pago conocido
          verifyLastTransaction();
        }
      }
    };
    
    window.addEventListener('message', handlePaymentMessage);
    
    // Extraer el ID de transacción de los parámetros de la URL
    const params = new URLSearchParams(location.search);
    const txid = params.get('txid');
    const status = params.get('status');
    const id = params.get('id'); // Payku puede enviar el ID como 'id'
    const token = params.get('token'); // Payku puede enviar un token
    
    console.log('Parámetros URL:', { txid, id, status, token });
    
    // Determinar el ID de transacción a verificar
    let transactionToVerify = '';
    
    // Prioridad 1: ID en la URL (puede venir como txid o id)
    if (txid) {
      transactionToVerify = txid;
      console.log('ID de transacción encontrado en URL como txid:', txid);
    } else if (id) {
      transactionToVerify = id;
      console.log('ID de transacción encontrado en URL como id:', id);
    } else if (token) {
      transactionToVerify = token;
      console.log('ID de transacción encontrado en URL como token:', token);
    } else {
      // Si no hay ID en la URL, intentar recuperarlo del localStorage
      const paykuData = localStorage.getItem('payku_transaction');
      if (paykuData) {
        try {
          const parsedData = JSON.parse(paykuData);
          if (parsedData.transaction_id) {
            transactionToVerify = parsedData.transaction_id;
            console.log('ID de transacción recuperado de localStorage (transaction_id):', transactionToVerify);
          } else if (parsedData.id) {
            transactionToVerify = parsedData.id;
            console.log('ID de transacción recuperado de localStorage (id):', transactionToVerify);
          }
        } catch (error) {
          console.error('Error al parsear datos de Payku del localStorage:', error);
        }
      }
      
      // Si todavía no tenemos ID, buscar en otras variables de localStorage
      if (!transactionToVerify) {
        const lastTxId = localStorage.getItem('last_payku_transaction');
        if (lastTxId) {
          transactionToVerify = lastTxId;
          console.log('ID de transacción recuperado de last_payku_transaction:', transactionToVerify);
        } else {
          const paykuTxId = localStorage.getItem('payku_transaction_id');
          if (paykuTxId) {
            transactionToVerify = paykuTxId;
            console.log('ID de transacción recuperado de payku_transaction_id:', transactionToVerify);
          }
        }
      }
    }
    
    // Si no tenemos transacción en la URL pero tenemos datos guardados,
    // verificar automáticamente la última transacción conocida
    if (!transactionToVerify) {
      // Comprobar si algún pago fue detectado recientemente
      const paymentDetected = localStorage.getItem('payku_payment_detected') === 'true';
      const detectionTime = parseInt(localStorage.getItem('payku_payment_detection_time') || '0');
      const isRecentDetection = Date.now() - detectionTime < 1000 * 60 * 5; // 5 minutos
      
      if (paymentDetected && isRecentDetection) {
        console.log('Pago detectado recientemente, verificando automáticamente');
        verifyLastTransaction();
      } else {
        setStatus('idle');
        setMessage('Ingresa el ID de transacción para verificar tu pago.');
      }
    }
    
    if (transactionToVerify) {
      setTransactionId(transactionToVerify);
      setIsAutoVerifying(true);
      verifyPayment(transactionToVerify);
    } else {
      setStatus('error');
      setMessage('No se pudo identificar la transacción. Por favor, contacta a soporte.');
    }
    
    // Limpiar datos temporales
    loadData();
    
    return () => {
      window.removeEventListener('message', handlePaymentMessage);
    };
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoadingTransactions(true);
      
      // Obtener el usuario actual
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Obtener transacciones recientes del usuario
        const { data: transactions, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) {
          console.error('Error al cargar transacciones:', error);
        } else if (transactions) {
          setRecentTransactions(transactions);
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const verifyPayment = async (id: string = transactionId) => {
    if (!id) {
      toast.error('Por favor, ingresa un ID de transacción');
      setStatus('error');
      setMessage('No se pudo identificar la transacción. Por favor, ingresa un ID de transacción válido.');
      return;
    }

    setStatus('loading');
    setMessage('Verificando pago...');
    setCurrentStep(2);

    try {
      console.log('Iniciando verificación de pago para ID:', id);
      
      // Verificar la transacción con Payku
      const paykuService = PaykuService.getInstance();
      console.log('Llamando a verifyTransaction con ID:', id);
      const result = await paykuService.verifyTransaction(id);
      console.log('Resultado de verificación:', result);

      if (result.success) {
        // Transacción exitosa
        setStatus('success');
        setMessage(result.message);
        toast.success('¡Pago verificado correctamente! Tus monedas han sido acreditadas.', {
          duration: 5000,
          icon: '🎉'
        });
        setCurrentStep(3);
        
        // Establecer información del paquete si está disponible
        if (result.details && result.details.transaction) {
          const tx = result.details.transaction;
          const packageData = {
            coins: result.details.coinsAdded || tx.vcoins_amount || 0,
            packageId: tx.package_id || 'basic',
            amount: tx.amount || 0
          };
          setPackageInfo(packageData);
        }
        
        // Limpiar datos de transacción pendiente
        localStorage.removeItem('pendingTransaction');
        sessionStorage.removeItem('pendingTransaction');
        localStorage.removeItem('payku_transaction');
        localStorage.removeItem('payku_payment_url');
        localStorage.removeItem('payku_return_url');
        
        // Marcar como completado para evitar verificaciones duplicadas
        localStorage.setItem('last_verified_transaction', id);
        localStorage.setItem('last_verification_timestamp', Date.now().toString());
        localStorage.setItem('last_verification_success', 'true');
        
        // Emitir evento de actualización de monedas
        const coinsUpdatedEvent = new CustomEvent('coins-updated');
        window.dispatchEvent(coinsUpdatedEvent);
        
        startRedirectCountdown();
        
        // Actualizar la lista de transacciones
        loadData();
      } else {
        // Error en la verificación
        console.error('Error en verificación:', result);
        
        // Mostrar mensaje según el tipo de error
        if (result.error === 'TRANSACTION_NOT_FOUND') {
          setStatus('error');
          setMessage('No se encontró la transacción en nuestros registros. Por favor, verifica el ID de transacción o contacta a soporte.');
          toast.error('Transacción no encontrada', { duration: 4000 });
        } else if (result.error === 'TRANSACTION_NOT_COMPLETED') {
          setStatus('loading');
          setMessage('El pago está pendiente de confirmación por parte de Payku. Por favor, intenta verificar nuevamente en unos minutos.');
          toast.info('Pago pendiente de confirmación', { duration: 4000 });
          
          // Incrementar el contador de intentos
          setVerificationAttempts(prev => prev + 1);
        } else {
          setStatus('error');
          setMessage(result.message || 'Error al verificar el pago. Por favor, contacta a soporte.');
          toast.error('Error al verificar el pago', { duration: 4000 });
        }
        
        // Si llevamos varios intentos, mostrar información adicional
        if (verificationAttempts >= 3) {
          toast((t) => (
            <div className="flex flex-col">
              <span className="font-medium mb-2">Verificación pendiente</span>
              <p className="text-sm">Hay problemas para verificar tu pago automáticamente. Puedes:</p>
              <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm">
                <li>Esperar unos minutos e intentar nuevamente</li>
                <li>Verificar manualmente con el ID de transacción</li>
                <li>Contactar a soporte con el ID: {id}</li>
              </ol>
              <button 
                onClick={() => {
                  verifyPayment(id);
                  toast.dismiss(t.id);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mt-3 text-center"
              >
                Intentar nuevamente
              </button>
            </div>
          ), {
            duration: 10000,
          });
        }
      }
    } catch (error) {
      console.error('Error al verificar pago:', error);
      setStatus('error');
      setMessage('Error inesperado al verificar el pago. Por favor, contacta a soporte.');
      toast.error('Error inesperado al verificar el pago');
    }
  };

  const startRedirectCountdown = () => {
    setCountdown(5);
    
    const interval = setInterval(() => {
      setCountdown(prevCountdown => {
        if (prevCountdown <= 1) {
          clearInterval(interval);
          // Redirigir al dashboard con la pestaña de monedas seleccionada
          navigate('/dashboard?tab=coins');
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Exitoso</span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pendiente</span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Fallido</span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><X className="w-3 h-3 mr-1" />Cancelado</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  // Función para marcar una transacción como cancelada
  const cancelTransaction = async (transactionId: string) => {
    try {
      setIsClearing(true);
      
      // Actualizar el estado de la transacción a 'cancelled' en la base de datos
      const { error } = await supabase
        .from('transactions')
        .update({ status: 'cancelled' })
        .eq('transaction_id', transactionId);
      
      if (error) {
        console.error('Error al cancelar la transacción:', error);
        toast.error('Error al cancelar la transacción');
        return false;
      }
      
      // Actualizar la lista de transacciones
      setRecentTransactions(prev => 
        prev.map(tx => 
          tx.transaction_id === transactionId 
            ? { ...tx, status: 'cancelled' } 
            : tx
        )
      );
      
      toast.success('Transacción marcada como cancelada');
      return true;
    } catch (error) {
      console.error('Error al cancelar la transacción:', error);
      toast.error('Error al cancelar la transacción');
      return false;
    } finally {
      setIsClearing(false);
    }
  };

  // Función para cancelar múltiples transacciones
  const cancelSelectedTransactions = async () => {
    if (selectedTransactions.length === 0) {
      toast.error('No hay transacciones seleccionadas');
      return;
    }
    
    setIsClearing(true);
    
    try {
      let successCount = 0;
      
      // Cancelar cada transacción seleccionada
      for (const txId of selectedTransactions) {
        const { error } = await supabase
          .from('transactions')
          .update({ status: 'cancelled' })
          .eq('transaction_id', txId);
        
        if (!error) {
          successCount++;
        }
      }
      
      // Actualizar la lista de transacciones
      setRecentTransactions(prev => 
        prev.map(tx => 
          selectedTransactions.includes(tx.transaction_id) 
            ? { ...tx, status: 'cancelled' } 
            : tx
        )
      );
      
      // Limpiar selección
      setSelectedTransactions([]);
      
      toast.success(`${successCount} transacciones canceladas correctamente`);
    } catch (error) {
      console.error('Error al cancelar transacciones:', error);
      toast.error('Error al cancelar transacciones');
    } finally {
      setIsClearing(false);
    }
  };

  // Función para limpiar sessionStorage
  const clearSessionStorage = () => {
    sessionStorage.removeItem('pendingTransaction');
    sessionStorage.removeItem('payku_transaction');
    toast.success('Información de transacciones limpiada del navegador');
  };

  // Función para seleccionar/deseleccionar todas las transacciones pendientes
  const toggleSelectAllPending = () => {
    if (selectedTransactions.length > 0) {
      // Si hay alguna seleccionada, deseleccionar todas
      setSelectedTransactions([]);
    } else {
      // Seleccionar todas las pendientes
      const pendingTxIds = recentTransactions
        .filter(tx => tx.status === 'pending')
        .map(tx => tx.transaction_id);
      
      setSelectedTransactions(pendingTxIds);
    }
  };

  // Función para manejar la selección de una transacción
  const toggleTransactionSelection = (txId: string) => {
    setSelectedTransactions(prev => 
      prev.includes(txId)
        ? prev.filter(id => id !== txId)
        : [...prev, txId]
    );
  };

  const verifyLastTransaction = useCallback(async () => {
    try {
      console.log('Iniciando verificación de la última transacción conocida');
      setStatus('loading');
      setMessage('Buscando información de pago...');
      
      // Verificar si ya tenemos un pago verificado recientemente (desde verify-payment.html)
      const paymentVerified = localStorage.getItem('payment_verified') === 'true';
      const verificationTimestamp = parseInt(localStorage.getItem('last_verification_timestamp') || '0');
      const isRecentVerification = Date.now() - verificationTimestamp < 300000; // 5 minutos
      
      if (paymentVerified && isRecentVerification) {
        // Obtener el ID de transacción ya verificado
        const verifiedTxId = localStorage.getItem('last_transaction_id');
        if (verifiedTxId) {
          console.log('Se encontró un pago recientemente verificado:', verifiedTxId);
          setStatus('success');
          setMessage('¡Tu pago ha sido verificado correctamente! Tus monedas han sido acreditadas.');
          startRedirectCountdown();
          
          // Limpiar los datos del pago verificado para evitar verificaciones duplicadas
          localStorage.removeItem('payment_verified');
          
          // Emitir evento de actualización de monedas
          const coinsUpdatedEvent = new CustomEvent('coins-updated');
          window.dispatchEvent(coinsUpdatedEvent);
          
          return;
        }
      }
      
      // 1. Intentar obtener el ID de transacción de la URL
      let transactionId = '';
      const params = new URLSearchParams(location.search);
      transactionId = params.get('txid') || params.get('id') || params.get('transaction_id') || '';
      
      if (transactionId) {
        console.log('ID de transacción obtenido desde parámetros URL:', transactionId);
      }
      
      // 2. Intentar obtener el ID de sessionStorage y localStorage en orden de prioridad
      if (!transactionId) {
        // Intentar diferentes fuentes en orden de prioridad
        const sources = [
          { key: 'payku_transaction', store: 'localStorage', field: 'transaction_id' },
          { key: 'last_payku_transaction', store: 'localStorage', direct: true },
          { key: 'last_payku_details', store: 'localStorage', field: 'id' },
          { key: 'payku_transaction_to_verify', store: 'sessionStorage', direct: true },
          { key: 'pendingTransaction', store: 'sessionStorage', field: 'id' },
          { key: 'pendingTransaction', store: 'localStorage', field: 'id' }
        ];
        
        for (const source of sources) {
          try {
            let value = null;
            
            // Obtener el valor según el almacenamiento
            if (source.store === 'localStorage') {
              value = localStorage.getItem(source.key);
            } else {
              value = sessionStorage.getItem(source.key);
            }
            
            if (!value) continue;
            
            // Procesar según si es directo o necesita parsing
            if (source.direct) {
              transactionId = value;
              console.log(`ID de transacción obtenido de ${source.store}.${source.key}:`, transactionId);
              break;
            } else {
              try {
                const parsed = JSON.parse(value);
                if (parsed && source.field && parsed[source.field]) {
                  transactionId = parsed[source.field];
                  console.log(`ID de transacción obtenido de ${source.store}.${source.key}.${source.field}:`, transactionId);
                  break;
                }
              } catch (e) {
                console.error(`Error al parsear ${source.key} desde ${source.store}:`, e);
              }
            }
          } catch (e) {
            console.error(`Error al acceder a ${source.key} en ${source.store}:`, e);
          }
        }
      }
      
      // Si tenemos un ID, verificar la transacción
      if (transactionId) {
        console.log('Iniciando verificación automática con ID:', transactionId);
        setTransactionId(transactionId);
        setMessage('Verificando transacción con Payku...');
        verifyPayment(transactionId);
        return;
      } 
      
      // 3. Si no tenemos ID, buscar las transacciones recientes del usuario en la base de datos
      console.log('No se encontró ID de transacción en almacenamiento local, consultando DB');
      setMessage('Buscando transacciones recientes...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStatus('error');
        setMessage('No hay usuario autenticado. Inicia sesión para verificar tu pago.');
        return;
      }
      
      // 3.1 Buscar transacciones pendientes
      const { data: pendingTransactions, error: pendingError } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (pendingError) {
        console.error('Error al buscar transacciones pendientes:', pendingError);
      } else if (pendingTransactions && pendingTransactions.length > 0) {
        // Verificar la transacción pendiente más reciente
        const latestTx = pendingTransactions[0];
        console.log('Transacción pendiente encontrada:', latestTx);
        
        if (latestTx.transaction_id) {
          setTransactionId(latestTx.transaction_id);
          setMessage(`Verificando transacción pendiente: ${latestTx.transaction_id}`);
          verifyPayment(latestTx.transaction_id);
          return;
        }
      } else {
        console.log('No se encontraron transacciones pendientes');
      }
      
      // 3.2 Buscar la transacción más reciente de las últimas 4 horas
      const fourHoursAgo = new Date(Date.now() - 14400000).toISOString();
      const { data: recentTransactions, error: recentError } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .gt('created_at', fourHoursAgo)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (recentError) {
        console.error('Error al buscar transacciones recientes:', recentError);
      } else if (recentTransactions && recentTransactions.length > 0) {
        const latestTx = recentTransactions[0];
        console.log('Transacción reciente encontrada:', latestTx);
        
        // Solo verificar si no está completada o cancelada
        if (latestTx.transaction_id && latestTx.status !== 'completed' && latestTx.status !== 'cancelled') {
          setTransactionId(latestTx.transaction_id);
          setMessage(`Verificando transacción reciente: ${latestTx.transaction_id}`);
          verifyPayment(latestTx.transaction_id);
          return;
        }
      }
      
      // 4. No se encontró ninguna transacción válida para verificar
      console.log('No se encontraron transacciones recientes para verificar');
      setStatus('idle');
      setMessage('No se encontró ninguna transacción reciente para verificar. Ingresa el ID de tu transacción manualmente o inicia un nuevo pago.');
      setCurrentStep(2); // Establecer paso en verificación manual
      
      // Mostrar toast solo si venimos de un proceso de pago
      if (comingFromPayku) {
        toast((t) => (
          <div className="flex flex-col">
            <span className="font-medium mb-2">Verificación pendiente</span>
            <p className="text-sm">No pudimos encontrar automáticamente los datos de tu transacción. Puedes:</p>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm">
              <li>Verificar manualmente ingresando el ID que recibiste de Payku</li>
              <li>Buscar en tus transacciones recientes</li>
              <li>Intentar nuevamente en unos momentos</li>
            </ol>
          </div>
        ), { duration: 8000 });
      }
    } catch (error) {
      console.error('Error al verificar última transacción:', error);
      setStatus('error');
      setMessage('Error al buscar la última transacción. Por favor, ingresa el ID manualmente.');
      
      // Mostrar error detallado en toast
      toast.error('Error al verificar la transacción: ' + (error instanceof Error ? error.message : 'Error desconocido'), {
        duration: 5000,
      });
    }
  }, [verifyPayment, startRedirectCountdown, location.search, comingFromPayku]);

  // Renderizar los pasos del proceso
  const renderSteps = () => {
    const steps = [
      { id: 1, name: 'Pago', icon: <CreditCard className="h-5 w-5" /> },
      { id: 2, name: 'Verificación', icon: <ShieldCheck className="h-5 w-5" /> },
      { id: 3, name: 'Confirmación', icon: <Coins className="h-5 w-5" /> }
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${currentStep >= step.id ? 'text-blue-500' : 'text-gray-500'}`}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${currentStep >= step.id ? 'border-blue-500 bg-blue-100' : 'border-gray-300'} mb-2`}>
                  {currentStep > step.id ? (
                    <CheckCircle2 className="h-6 w-6" />
                  ) : currentStep === step.id && status === 'loading' ? (
                    <div className="animate-spin">
                      <Loader className="h-5 w-5" />
                    </div>
                  ) : (
                    step.icon
                  )}
                </div>
                <span className={`text-sm font-medium ${currentStep >= step.id ? 'text-blue-500' : 'text-gray-500'}`}>
                  {step.name}
                </span>
              </div>
              
              {index < steps.length - 1 && (
                <div className={`w-12 h-1 mx-2 ${currentStep > index + 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Renderizar información del paquete
  const renderPackageInfo = () => {
    if (!packageInfo) return null;
    
    return (
      <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
        <h3 className="text-lg font-medium text-blue-800 mb-2">Detalles de la compra</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Paquete</p>
            <p className="font-medium">{packageInfo.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Monedas</p>
            <p className="font-medium">{packageInfo.vcoins}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Monto</p>
            <p className="font-medium">${packageInfo.amount.toLocaleString('es-CL')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">ID de Transacción</p>
            <p className="font-mono text-xs truncate">{transactionId}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorState = () => (
    <div className="text-center">
      <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-4 mx-auto">
        <AlertCircle className="w-12 h-12 text-red-500" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Verificación fallida</h2>
      <p className="text-gray-300 mb-6">{message}</p>
      
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => navigate('/account')}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
        >
          <span>Volver a mi cuenta</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        
        {/* Botón para volver a Payku si hay una URL guardada */}
        {localStorage.getItem('payku_payment_url') && (
          <button
            onClick={() => {
              const paymentUrl = localStorage.getItem('payku_payment_url');
              if (paymentUrl) {
                window.open(paymentUrl, '_self');
              }
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
          >
            <span>Volver a la página de pago</span>
            <CreditCard className="w-4 h-4" />
          </button>
        )}
        
        <button
          onClick={() => verifyPayment(transactionId)}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
        >
          <span>Verificar nuevamente</span>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      <p className="text-sm text-gray-400 mt-6">
        Si realizaste un pago y no se ha reflejado en tu cuenta, por favor contacta a soporte con el ID de transacción: <span className="text-white font-mono">{transactionId || 'No disponible'}</span>
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">Verificación de Pago</h1>
            
            {/* Pasos del proceso */}
            {renderSteps()}
            
            {/* Información del paquete */}
            {renderPackageInfo()}
            
            {/* Botón de verificación manual grande */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-8 shadow-md">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">¿Acabas de completar un pago en Payku?</h2>
              <p className="text-blue-700 mb-4">
                Si has completado un pago pero no has sido redirigido automáticamente, haz clic en el botón para verificar tu pago:
              </p>
              <button
                onClick={verifyLastTransaction}
                disabled={status === 'loading'}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition duration-300 flex items-center justify-center text-lg"
              >
                {status === 'loading' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                    Verificando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Verificar mi pago
                  </>
                )}
              </button>
              <p className="text-sm text-blue-600 mt-3 text-center">
                Este botón verificará automáticamente tu última transacción de pago.
              </p>
            </div>
            
            {/* Alerta para usuarios que vienen de Payku */}
            {isAutoVerifying && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-lg font-medium text-blue-800">Verificación automática en progreso</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        Estamos verificando tu pago automáticamente. Este proceso puede tomar unos segundos.
                      </p>
                    </div>
                    <div className="mt-4">
                      <div className="w-full bg-blue-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Verificación manual</h2>
              
              <p className="text-gray-600 mb-6">
                Si has realizado un pago pero no has sido redirigido automáticamente, puedes verificar tu pago manualmente ingresando el ID de transacción.
              </p>
              
              <div className="mb-6">
                <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 mb-2">
                  ID de Transacción
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="transactionId"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ingresa el ID de transacción"
                  />
                  <button
                    onClick={() => verifyPayment()}
                    disabled={status === 'loading'}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-r-lg transition duration-300 flex items-center"
                  >
                    {status === 'loading' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    <span>Verificar</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  El ID de transacción se encuentra en el comprobante de pago o en el correo electrónico que recibiste.
                </p>
              </div>
              
              {status === 'loading' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-3"></div>
                    <p className="text-blue-700">{message}</p>
                  </div>
                </div>
              )}
              
              {status === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-green-700">{message}</p>
                      <p className="text-sm text-green-600 mt-2">
                        Serás redirigido al dashboard en {countdown} segundos...
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {status === 'error' && (
                renderErrorState()
              )}
            </div>
            
            {/* Herramientas de administración */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Herramientas de administración</h2>
              
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={clearSessionStorage}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium py-2 px-3 rounded transition duration-300 flex items-center"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Limpiar datos de sesión
                </button>
                
                <button
                  onClick={toggleSelectAllPending}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium py-2 px-3 rounded transition duration-300 flex items-center"
                >
                  {selectedTransactions.length > 0 ? (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Deseleccionar todo
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Seleccionar pendientes
                    </>
                  )}
                </button>
                
                <button
                  onClick={cancelSelectedTransactions}
                  disabled={selectedTransactions.length === 0 || isClearing}
                  className={`text-white text-sm font-medium py-2 px-3 rounded transition duration-300 flex items-center ${
                    selectedTransactions.length === 0 || isClearing
                      ? 'bg-red-300 cursor-not-allowed'
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {isClearing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Cancelar seleccionados ({selectedTransactions.length})
                </button>
              </div>
            </div>
            
            {/* Transacciones recientes */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Transacciones recientes</h2>
                {selectedTransactions.length > 0 && (
                  <span className="text-sm text-yellow-600">
                    {selectedTransactions.length} transacciones seleccionadas
                  </span>
                )}
              </div>
              
              {loadingTransactions ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : recentTransactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.length > 0}
                            onChange={toggleSelectAllPending}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentTransactions.map((tx) => (
                        <tr key={tx.id} className={`hover:bg-gray-50 ${selectedTransactions.includes(tx.transaction_id) ? 'bg-blue-50' : ''}`}>
                          <td className="px-2 py-3 text-sm">
                            {tx.status !== 'success' && (
                              <input
                                type="checkbox"
                                checked={selectedTransactions.includes(tx.transaction_id)}
                                onChange={() => toggleTransactionSelection(tx.transaction_id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono">{tx.transaction_id.substring(0, 12)}...</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(tx.created_at)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{tx.amount} {tx.currency}</td>
                          <td className="px-4 py-3 text-sm">{getStatusBadge(tx.status)}</td>
                          <td className="px-4 py-3 text-sm">
                            {tx.status === 'pending' && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => verifyPayment(tx.transaction_id)}
                                  className="text-blue-600 hover:text-blue-800 flex items-center"
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  <span>Verificar</span>
                                </button>
                                <button
                                  onClick={() => cancelTransaction(tx.transaction_id)}
                                  className="text-red-600 hover:text-red-800 flex items-center"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  <span>Cancelar</span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No se encontraron transacciones recientes.</p>
                </div>
              )}
            </div>
            
            <div className="flex flex-col space-y-4 mt-8">
              <Link 
                to="/coins" 
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
              >
                <span>Volver a Comprar Monedas</span>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              
              <Link 
                to="/dashboard" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
              >
                <span>Volver al Dashboard</span>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyPayment; 